import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Row } from 'antd';
import { Rule } from 'antd/lib/form';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ISubj } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import {
    ECrsStav,
    ICrsDotaznik,
    ICrsOsoba,
    crsOsobaDelete,
    crsOsobaGet,
    crsOsobaSend,
    crsOsobyGet
} from 'services/poDlt/crs';

import { EAnoNe } from 'enums/anoNe';

import anoNe from 'data/codebooks/anoNeSelect';
import crsStatusSelect from 'data/codebooks/podlt/crsStatusSelect';
import crsTypOsobySelect from 'data/codebooks/podlt/crsTypOsobySelect';

import pscValidation from 'utils/validations/pscValidation';

interface ICrsOsobaPage {
    prevStep: () => void;
    nextStep: () => void;
    zadostID: number;
    dotaznikData: ICrsDotaznik;
}

export const dicOrReasonRule = (dic?: string, dicduvod?: string): Rule => ({
    validator() {
        const hasDic = !!dic;
        const hasReason = !!dicduvod;

        if (!hasDic && !hasReason) {
            return Promise.reject(<TranslateText>CRS_OSOBA_DIC_ERR</TranslateText>);
        }

        return Promise.resolve();
    }
});
const CrsOsoba: React.FC<ICrsOsobaPage> = ({ prevStep, nextStep, zadostID, dotaznikData }) => {
    const { cisStaty } = useContext(Context);
    const [form] = Form.useForm();
    const [refetch, setRefetech] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [osobyData, setOsobyData] = useState<{ osoby: ICrsOsoba[] }>();
    const [personData, setPersonData] = useState<ICrsOsoba>();
    const [adressAlt, setAddressAlt] = useState<boolean>(false);
    const viewOnly = dotaznikData?.stav === ECrsStav.ZPRAC;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const dataToSend = {
            ...fieldsValue,
            osobastaty: fieldsValue.osobastaty.map((e: string) => {
                return {
                    stat: e
                };
            }),
            datumnar: fieldsValue.datumnar.format('YYYY-MM-DD'),
            madoruc: !!fieldsValue.advy
        };
        setLoading(true);
        crsOsobaSend(personData?.id ? 'PUT' : 'POST', dataToSend, zadostID, personData?.id)
            .then(() => {
                setPersonData(undefined);
                setRefetech(pv => !pv);
            })
            .finally(() => setLoading(true));
    };

    useEffect(() => {
        setLoading(true);
        crsOsobyGet(zadostID)
            .then(resp => {
                setOsobyData(resp);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch]);

    const columns = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno'
        },
        {
            title: <TranslateText>PRIJMENI</TranslateText>,
            dataIndex: 'prijmeni',
            key: 'prijmeni'
        },
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'typ',
            key: 'typ',
            render: (typ: string) => <TranslateText>{`CRS_OSOBA_${typ}`}</TranslateText>
        },
        {
            align: 'center' as const,
            width: 190,
            dataIndex: 'id',
            key: 'id',
            render: (id: number, _row: ISubj, index: number) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-around'
                        }}
                    >
                        <AnchorComponent
                            id={`CrsDoplnitBtn${index}`}
                            key={index + 'b'}
                            onClick={() => {
                                setLoading(true);
                                crsOsobaGet(zadostID, id)
                                    .then(resp => {
                                        setPersonData(resp);
                                        setAddressAlt(resp.madoruc);
                                    })
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <TranslateText>{viewOnly ? 'ZOBRAZIT' : 'BUTTON_UPRAVIT'}</TranslateText>
                        </AnchorComponent>

                        {!viewOnly && (
                            <AnchorComponent
                                id={`CrsOdebratBtn${index}`}
                                key={index}
                                onClick={() => {
                                    setLoading(true);
                                    crsOsobaDelete(zadostID, id)
                                        .then(() => setRefetech(pv => !pv))
                                        .finally(() => setLoading(false));
                                }}
                            >
                                <TranslateText>ODEBRAT</TranslateText>
                            </AnchorComponent>
                        )}
                    </div>
                );
            }
        }
    ];
    return (
        <LoadingWrapper loading={loading}>
            {personData ? (
                <FormWrapper onFinish={onFinish} name="osoba" outForm={form} preserve={false}>
                    <Title level={5} dividerTitle={true} text="OSOBA" />
                    <SelectComponent
                        name="typ"
                        labelParagraph="CRS_OSOBA"
                        isFormItem={true}
                        defaultValue={personData?.typ}
                        data={crsTypOsobySelect}
                        showOptionValue={true}
                        disabled={viewOnly}
                    />
                    <InputComponent
                        label="FORMULARE_JMENA"
                        name="jmeno"
                        initialValue={personData?.jmeno}
                        maxLength={100}
                        disabled={viewOnly}
                    />
                    <InputComponent
                        label="FORMULARE_PRIJMENI"
                        name="prijmeni"
                        initialValue={personData?.prijmeni}
                        maxLength={100}
                        disabled={viewOnly}
                    />
                    <SelectComponent
                        name="stat"
                        isFormItem={true}
                        labelParagraph="FORMULARE_STAT_NAROZENI"
                        defaultValue={personData?.stat}
                        data={cisStaty}
                        disabled={viewOnly}
                    />
                    <InputComponent
                        label="FORMULARE_MISTO_NAROZENI"
                        name="misto"
                        initialValue={personData?.misto}
                        maxLength={50}
                        disabled={viewOnly}
                    />
                    <DatePickerComponent
                        name="datumnar"
                        label="FORMULARE_DATUM_NAROZENI"
                        initialValue={personData?.datumnar ? dayjs(personData?.datumnar) : undefined}
                        disabledDate={current => current > dayjs()}
                        disabled={viewOnly}
                    />
                    <SelectComponent
                        name="osobastaty"
                        isFormItem={true}
                        labelParagraph="FORMULARE_STATNI_OBCANSTVI"
                        mode="multiple"
                        data={cisStaty}
                        disabled={viewOnly}
                        defaultValue={(personData.osobastaty || []).map(e => e.stat)}
                    />
                    <Title level={5} dividerTitle={true} text="CRS_STAT_TAX_OSOBY" />
                    <Paragraph
                        text="CRS_OSOBA_BYDLISTE_DAN_INFO1"
                        style={{
                            display: 'block'
                        }}
                    />
                    <Paragraph
                        text="CRS_OSOBA_BYDLISTE_DAN_INFO2"
                        style={{
                            display: 'block',
                            marginBottom: 16
                        }}
                    />
                    <Form.List
                        name="osobazeme"
                        initialValue={
                            personData.osobazeme || [
                                {
                                    stat: undefined,
                                    dic: undefined,
                                    dicduvod: undefined
                                }
                            ]
                        }
                    >
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <React.Fragment key={field.key}>
                                        <SelectComponent
                                            isFormItem={true}
                                            name={[field.name, 'stat']}
                                            labelParagraph="ZEME"
                                            data={cisStaty}
                                            disabled={viewOnly}
                                            labelExtra={
                                                (fields.length === 3 && index === 0 ? false : true) && (
                                                    <Button
                                                        type="primary"
                                                        icon={index === 0 ? <PlusOutlined /> : <MinusOutlined />}
                                                        onClick={() => {
                                                            if (index === 0) {
                                                                add({
                                                                    stat: undefined,
                                                                    dic: undefined,
                                                                    dicduvod: undefined
                                                                });
                                                            } else {
                                                                remove(field.name);
                                                            }
                                                        }}
                                                        style={{
                                                            display: 'inline-block',
                                                            marginLeft: 20,
                                                            width: 50
                                                        }}
                                                        disabled={viewOnly}
                                                    />
                                                )
                                            }
                                        />

                                        <Form.Item
                                            dependencies={[
                                                ['osobazeme', index, 'stat'],
                                                ['osobazeme', index, 'dic'],
                                                ['osobazeme', index, 'dicduvod']
                                            ]}
                                            noStyle={true}
                                        >
                                            {({ getFieldValue }) => {
                                                const stat = getFieldValue(['osobazeme', index, 'stat']);
                                                const dic = getFieldValue(['osobazeme', index, 'dic']);
                                                const dicduvod = getFieldValue(['osobazeme', index, 'dicduvod']);
                                                if (!stat || stat === 'CZ') return null;

                                                return (
                                                    <>
                                                        <InputComponent
                                                            infoTooltipText="CRS_DIC_TOOLTIP"
                                                            label="DIC"
                                                            name={[field.name, 'dic']}
                                                            maxLength={20}
                                                            notRequired={true}
                                                            dependencies={[['osobazeme', index, 'dicduvod']]}
                                                            rules={[dicOrReasonRule(dic, dicduvod)]}
                                                            disabled={viewOnly || !!dicduvod}
                                                        />
                                                        <SelectComponent
                                                            name={[field.name, 'dicduvod']}
                                                            isFormItem={true}
                                                            notRequired={true}
                                                            labelParagraph="CRS_NEVYPLNENI_DIC_DUVOD"
                                                            rules={[dicOrReasonRule(dic, dicduvod)]}
                                                            dependencies={[['osobazeme', index, 'dic']]}
                                                            data={[
                                                                {
                                                                    labelcz: 'CRS_DICDUVOD_A',
                                                                    value: 'A'
                                                                },
                                                                {
                                                                    labelcz: 'CRS_DICDUVOD_B',
                                                                    value: 'B'
                                                                },
                                                                {
                                                                    labelcz: 'CRS_DICDUVOD_C',
                                                                    value: 'C'
                                                                }
                                                            ]}
                                                            disabled={viewOnly || !!dic}
                                                        />
                                                    </>
                                                );
                                            }}
                                        </Form.Item>
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </Form.List>
                    <Title level={5} dividerTitle={true} text="FORMULARE_ADRESA_POBYTU" />
                    <InputComponent
                        label="ULICE_CP"
                        name="adma"
                        initialValue={personData?.adma}
                        maxLength={50}
                        disabled={viewOnly}
                    />
                    <InputComponent
                        label="MESTO"
                        name="mema"
                        initialValue={personData?.mema}
                        maxLength={50}
                        disabled={viewOnly}
                    />
                    <InputComponent
                        label="PSC"
                        name="pscm"
                        initialValue={personData?.pscm}
                        rules={pscValidation('kost')}
                        maxLength={10}
                        disabled={viewOnly}
                    />
                    <SelectComponent
                        isFormItem={true}
                        name="kost"
                        labelParagraph="STAT"
                        defaultValue={personData?.kost}
                        data={cisStaty}
                        disabled={viewOnly}
                    />
                    <CheckboxComponent
                        name="madoruc"
                        checkboxText={
                            <Row>
                                <Title level={4} text="CRS_OSOBY_ADRESA_ALT" />
                            </Row>
                        }
                        onChange={() => setAddressAlt(pv => !pv)}
                        defaultChecked={adressAlt}
                        checked={adressAlt}
                        style={{
                            marginBottom: 4,
                            marginTop: 10
                        }}
                        disabled={viewOnly}
                        notRequired={true}
                    />
                    {adressAlt && (
                        <>
                            <InputComponent
                                label="ULICE_CP"
                                name="advy"
                                maxLength={50}
                                initialValue={personData?.advy}
                                disabled={viewOnly}
                            />

                            <InputComponent
                                label="MESTO"
                                name="mevy"
                                maxLength={50}
                                initialValue={personData?.mevy}
                                disabled={viewOnly}
                            />
                            <InputComponent
                                label="PSC"
                                name="psvy"
                                maxLength={10}
                                rules={pscValidation('ksvy')}
                                initialValue={personData?.psvy}
                                disabled={viewOnly}
                            />
                            <SelectComponent
                                name="ksvy"
                                labelParagraph="STAT_SIDLA"
                                isFormItem={true}
                                defaultValue={personData?.ksvy}
                                data={cisStaty}
                                disabled={viewOnly}
                            />
                        </>
                    )}
                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="ZPET"
                            onClick={() => {
                                setAddressAlt(false);
                                setPersonData(undefined);
                            }}
                            id="osobaZpetBtn"
                            type="default"
                        />
                        {!viewOnly && (
                            <ButtonComponent buttontext="POKRACOVAT" id="osobaPokracovatBtn" htmlType="submit" />
                        )}
                    </Row>
                </FormWrapper>
            ) : (
                <>
                    <SelectComponent
                        disabled={true}
                        name="stat"
                        labelParagraph="CRS_STAT_TAX"
                        isFormItem={true}
                        defaultValue={dotaznikData?.stat}
                        data={cisStaty}
                    />
                    <SelectComponent
                        disabled={true}
                        name="osobycr"
                        labelParagraph="CRS_OSOBY_CR"
                        isFormItem={true}
                        defaultValue={
                            typeof dotaznikData?.osobycr === 'boolean'
                                ? dotaznikData?.osobycr
                                    ? EAnoNe.ANO
                                    : EAnoNe.NE
                                : undefined
                        }
                        data={anoNe}
                    />
                    {dotaznikData?.statut && (
                        <SelectComponent
                            disabled={true}
                            name="statut"
                            labelParagraph="CRS_STATUS"
                            isFormItem={true}
                            defaultValue={dotaznikData?.statut}
                            data={crsStatusSelect}
                        />
                    )}
                    <Title
                        level={5}
                        text={'CRS_OSOBY'}
                        style={{
                            margin: 0,
                            marginTop: 10
                        }}
                    />
                    <TableComponent
                        locale={{
                            emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                        }}
                        rowClassName={(_a, i) => {
                            const classes = [];
                            if (i % 2) classes.push('tableRowGray');
                            return classes.join(' ');
                        }}
                        style={{
                            margin: '30px 0px'
                        }}
                        rowKey={(_, index) => index || 0}
                        columns={columns}
                        dataSource={osobyData?.osoby}
                        pagination={false}
                        footer={
                            viewOnly
                                ? undefined
                                : () => (
                                      <AnchorComponent
                                          id="osobaPridatOsobu"
                                          style={{
                                              textAlign: 'center',
                                              display: 'block'
                                          }}
                                          onClick={() => {
                                              setAddressAlt(false);
                                              form.resetFields();
                                              setPersonData({} as ICrsOsoba);
                                          }}
                                      >
                                          <TranslateText>PRIDAT_OSOBU</TranslateText>
                                      </AnchorComponent>
                                  )
                        }
                    />

                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="ZPET"
                            onClick={() => prevStep()}
                            id="osobaZpetBtn"
                            type="default"
                        />
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            id="osobaPokracovatBtn"
                            disabled={(osobyData?.osoby || []).length === 0}
                            type="primary"
                            onClick={() => nextStep()}
                        />
                    </Row>
                </>
            )}
        </LoadingWrapper>
    );
};
export default CrsOsoba;
